import { useRef, useEffect, useMemo, useCallback, useState } from 'react'
import debounce from 'lodash.debounce'
import { validate } from 'uuid'
import { cn } from '@/utils'
import { DEFAULT_ANNOTATIONS_MENUS } from '@/pages/DocumentPage/components/Annotations/menus'
import {
  DEFAULT_STROKE_COLOR,
  DISABLED_STROKE_COLOR,
} from '@/pages/DocumentPage/components/Annotations/constants'
import {
  AnnotationSizes,
  IconStrokes,
} from '@/pages/DocumentPage/components/Annotations/enums'
import { KonvaAnnotationPreview } from '@/pages/DocumentPage/components/Annotations/KonvaAnnotationPreview'
import { useDocumentState } from '@/state'
import { CreateDocumentVersionButton } from '@/components/versions/CreateDocumentVersionButton'
import { useDocumentPageContextQuery } from '@/pages/DocumentPage/queries'
import { CommentsButton } from '@/pages/DocumentPage/components/CommentsButton'
import { CreateDocumentThemeButton } from '@/pages/DocumentPage/components/CreateDocumentThemeButton'
import { OpenTechnicianModeButton } from '@/pages/TechnicianPage/components/OpenTechnicianModeButton'
import { useDocumentThemeFlag } from '@/featureFlags/useDocumentThemeFlag'
import { BubblesMenuItem } from '@/pages/DocumentPage/components/Annotations/BubblesMenuItem'
// import { InsertElementMenu } from './InsertElementMenu' // Kept for future reference
import { ScreenshotMenu } from './ScreenshotMenu'
import { MediaMenu } from './MediaMenu'
import { TextMenu } from './TextMenu'
import { TableMenu } from './TableMenu'
import { AnnotationsToolbarMenu } from '../Annotations/AnnotationsToolbarMenu'
import { useDraggableElements } from '../../hooks'
import { useIsReadOnly } from '@/pages/CADPage/hooks/useIsReadOnly'

export const DocumentPageToolbar = () => {
  const { data: readOnlyData } = useIsReadOnly()
  const isReadOnly = readOnlyData.isReadOnly

  const documentPage = useDocumentPageContextQuery()
  const [toolbarMenus, setToolbarMenus] = useState(DEFAULT_ANNOTATIONS_MENUS)
  const currentPageInView = useDocumentState((state) => state.currentPageInView)
  const setMenuRefPosition = useDocumentState(
    (state) => state.setMenuRefPosition,
  )
  const documentToolbarRef = useRef<HTMLDivElement>(null)
  const newElementData = useDocumentState((state) => state.newElementData)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const canDropElement = useDocumentState((state) => state.canDropElement)
  const {
    Component: IconPlaceholder,
    dataType: iconPlaceholderDataType,
    attributes: iconPlaceholderAttributes,
  } = newElementData || {}

  const iconPlaceholderType =
    iconPlaceholderDataType === 'annotation'
      ? iconPlaceholderAttributes?.type
      : iconPlaceholderDataType
  const iconPlaceholderSize = iconPlaceholderAttributes?.size
  const iconPlaceholderColor = iconPlaceholderAttributes?.strokeColor
  const iconPlaceholderStartPoint = iconPlaceholderAttributes?.startPoint
  const iconPlaceholderPreset = iconPlaceholderAttributes?.preset
  const iconPlaceholderStrokeColor = canDropElement
    ? iconPlaceholderColor || DEFAULT_STROKE_COLOR
    : DISABLED_STROKE_COLOR

  const {
    data: { isEnabled: isDocumentThemeFlagEnabled },
  } = useDocumentThemeFlag()

  useDraggableElements()

  useEffect(() => {
    setToolbarMenus((prev) => {
      const bubblesMenu = {
        id: 'bubbles',
        columns: 4,
        TriggerIcon: BubblesMenuItem('A') as any,
        menuItems: [] as any,
        tooltipContent: 'Bubble annotations',
        label: '',
      }
      const menuItems = Array.from(Array(26)).map((_, index) => ({
        id: `bubble-${index + 1}`,
        Icon: BubblesMenuItem(`${String.fromCharCode(65 + index)}`) as any,
        strokeColor: 'black',
        bubbleValue: String.fromCharCode(65 + index),
      }))
      bubblesMenu.menuItems = menuItems
      return [...prev, bubblesMenu]
    })

    return () => {
      setToolbarMenus(DEFAULT_ANNOTATIONS_MENUS)
    }
  }, [])

  const onResizeHandler = useCallback(
    () =>
      debounce(([entry]: Array<ResizeObserverEntry>) => {
        const rect = entry.target.getBoundingClientRect()
        setMenuRefPosition({ x: rect.x, y: rect.y })
      }, 300),
    [setMenuRefPosition],
  )

  const resizeObserver = useMemo(
    () => new ResizeObserver(onResizeHandler()),
    [onResizeHandler],
  )

  useEffect(() => {
    const documentToolbar = documentToolbarRef.current
    if (documentToolbar) {
      resizeObserver.observe(documentToolbar)
    }

    return () => {
      if (documentToolbar) resizeObserver.unobserve(documentToolbar)
    }
  }, [documentToolbarRef, resizeObserver])

  return (
    <div
      className="flex absolute z-30 top-0 left-0 w-full bg-white min-h-12 h-auto py-1 shadow-md shadow-stone-200/70"
      ref={documentToolbarRef}
    >
      {isAddingElement && IconPlaceholder && (
        <div
          className={cn(
            'fixed z-50 pointer-events-none box-content border-2 border-transparent',
            'top-0 left-0',
            'flex items-center justify-center',
            iconPlaceholderSize &&
              (AnnotationSizes[iconPlaceholderSize] ??
                `h-${iconPlaceholderSize} w-${iconPlaceholderSize}`),
          )}
          id={`icon-placeholder--${iconPlaceholderType}`}
        >
          {!iconPlaceholderStartPoint && (
            <>
              {iconPlaceholderDataType === 'annotation' &&
              iconPlaceholderPreset ? (
                <KonvaAnnotationPreview
                  preset={iconPlaceholderPreset}
                  size={iconPlaceholderSize}
                  strokeColor={iconPlaceholderStrokeColor}
                />
              ) : (
                <IconPlaceholder
                  className="h-full w-full stroke-1 opacity-60"
                  strokeWidth={
                    IconStrokes[iconPlaceholderSize || ''] ??
                    IconStrokes['small']
                  }
                  strokeColor={iconPlaceholderStrokeColor}
                />
              )}
            </>
          )}
        </div>
      )}
      <div className="flex items-center mx-4 gap-1 flex-1 justify-center">
        <ScreenshotMenu
          isDisabled={
            !validate(currentPageInView) ||
            isAddingElement ||
            !documentPage.data?.isLatestDocumentVersion ||
            isReadOnly
          }
        />
        <MediaMenu
          isDisabled={
            !validate(currentPageInView) ||
            isAddingElement ||
            !documentPage.data?.isLatestDocumentVersion ||
            isReadOnly
          }
          documentType={documentPage.data?.documentType}
        />
        <TextMenu
          isDisabled={
            !validate(currentPageInView) ||
            isAddingElement ||
            !documentPage.data?.isLatestDocumentVersion ||
            isReadOnly
          }
        />
        <TableMenu
          isDisabled={
            !validate(currentPageInView) ||
            isAddingElement ||
            !documentPage.data?.isLatestDocumentVersion ||
            isReadOnly
          }
        />
        {toolbarMenus.map((menu) => (
          <AnnotationsToolbarMenu
            key={menu.id}
            menuConfig={menu}
            isDisabled={
              !validate(currentPageInView) ||
              isAddingElement ||
              !documentPage.data?.isLatestDocumentVersion ||
              isReadOnly
            }
          />
        ))}
        <CommentsButton isDisabled={!validate(currentPageInView)} />

        {isDocumentThemeFlagEnabled &&
          documentPage.data?.documentType === 'work_instructions' && (
            <CreateDocumentThemeButton
              isDisabled={!documentPage.data?.isLatestDocumentVersion}
              documentPageId={currentPageInView}
              documentVersionId={documentPage.data?.documentVersion?.id}
            />
          )}
        <OpenTechnicianModeButton />
        <CreateDocumentVersionButton
          className="rounded-md hover:bg-gray-100 m-0 h-full flex p-0 items-center justify-center"
          disabled={!documentPage.data?.isLatestDocumentVersion || isReadOnly}
          lastestVersionNumber={
            documentPage.data.latestDocumentVersion?.version_number || 0
          }
          documentVersionId={documentPage.data.latestDocumentVersion?.id}
        />
      </div>
    </div>
  )
}
